import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TextField, Autocomplete, Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import config from '../config';
import useMediaQuery from '@mui/material/useMediaQuery';
import { fetchConditions } from '../Utils';
import { logAnalyticsEvent } from '../utils/analytics';

function Search({ itemKey, dataKey, detailKey, routeBase, label, onSearchFocus }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadItems = async () => {
      if (dataKey === 'conditions') {
        const conditionsData = await fetchConditions(config);
        setItems(conditionsData);
      } else {
        fetchItems();
      }
    };
    loadItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${config.FIREBASE_STORAGE_BASE_URL}config%2F${dataKey}.json?alt=media`);
      const data = response.data;
      
      // Ensure data is an array before sorting
      const sortedItems = Array.isArray(data) ? data.sort((a, b) => a.name.localeCompare(b.name)) : [];
      setItems(sortedItems);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const handleItemChange = (event, value) => {
    if (onSearchFocus) {
      onSearchFocus(false);
    }
    if (value) {
      // Extract the current tab from the path to maintain tab state
      const currentPath = window.location.pathname;
      let activeTab = null;
      
      // Check for all possible tabs in the current path
      if (currentPath.includes('/research')) {
        activeTab = 'research';
      } else if (currentPath.includes('/products')) {
        activeTab = 'products';
      } else if (currentPath.includes('/stacks')) {
        activeTab = 'stacks';
      } else if (currentPath.includes('/interactions')) {
        activeTab = 'interactions';
      } else if (currentPath.includes('/reviews')) {
        activeTab = 'reviews';
      }
      
      // Build the new path maintaining the active tab
      // Use value.key directly without any manipulation
      let newPath = `/${routeBase}/${value.key}`;
      if (activeTab) {
        newPath += `/${activeTab}`;
      }

      logAnalyticsEvent('library_search_result_selected', {
        data_key: dataKey,
        route_base: routeBase,
        selected_key: value.key,
        selected_name: value.name,
        active_tab: activeTab || 'details',
        current_path: currentPath,
      });
      
      navigate(newPath);
      
      // Reset both the input value and search query
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
        searchInputRef.current.blur();
      }
      setSearchQuery('');
    }
  };

  const handleFocus = () => {
    setIsFocused(false);
    if (onSearchFocus) {
      onSearchFocus(false); // Center the search bar
    }
  };

  const handleBlur = () => {
    if (!searchQuery) {
      setIsFocused(false);
      if (onSearchFocus) {
        onSearchFocus(false); // Reset search bar position if no query
      }
    }
  };

  return (
    <Box
      sx={{
        width: { xs: '92%', sm: '75%', md: '65%' },
        maxWidth: '37.5rem',
        margin: '0 auto',
        position: 'relative',
        transition: 'all 0.3s ease',
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.875rem',
          backgroundColor: 'background.paper',
          boxShadow: '0 0.25rem 1rem rgba(0,0,0,0.04)',
          border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
          minHeight: isMobile ? '3.5rem' : '3rem',
          '&:hover': {
            boxShadow: `0 0.375rem 1.25rem ${theme.surfaces.brandShadow}`,
            border: `0.0625rem solid ${theme.palette.primary.light}`,
          },
          '&.Mui-focused': {
            boxShadow: `0 0.375rem 1.25rem ${theme.surfaces.brandShadow}`,
            border: `0.0625rem solid ${theme.palette.primary.light}`,
          }
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none'
        },
        '& .MuiAutocomplete-input': {
          padding: '0.75rem 0.875rem !important',
        },
        '& .MuiInputLabel-root': {
          color: 'text.secondary',
          backgroundColor: 'background.paper',
          padding: '0 0.5rem',
          '&.Mui-focused': {
            color: theme.palette.primary.light
          }
        }
      }}
    >
      <Autocomplete
        disablePortal={!isMobile}
        fullWidth
        blurOnSelect={isMobile}
        options={items}
        getOptionLabel={(option) => option.name}
        onChange={handleItemChange}
        value={null}
        inputValue={searchQuery}
        onInputChange={(event, newInputValue) => {
          setSearchQuery(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
            inputRef={searchInputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={isMobile ? "Tap to search..." : "Start typing to search..."}
          />
        )}
        PaperComponent={({ children, ...props }) => (
          <Paper
            {...props}
            elevation={8}
            sx={{
              borderRadius: '0.75rem',
              mt: 1,
              border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
              '& .MuiAutocomplete-listbox': {
                padding: '0.5rem',
                '& .MuiAutocomplete-option': {
                  borderRadius: '0.5rem',
                  margin: '0.25rem 0',
                  padding: isMobile ? '0.75rem 1rem' : '0.5rem 1rem',
                  '&[aria-selected="true"]': {
                    backgroundColor: theme.surfaces.brandTintSm,
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.surfaces.brandTintSm,
                  }
                }
              }
            }}
          >
            {children}
          </Paper>
        )}
      />
    </Box>
  );
}

export default Search;